import { ArticleHeading2 } from '@cian/ui-kit';

import { clsx } from 'clsx';
import { FC, useCallback, useMemo, useState } from 'react';

import { WorkplaceInCoworkingType } from 'shared/enums/WorkplaceInCoworkingType';
import { useSelectedCoworkingTypeSelector } from 'shared/hooks/selectors/coworking/useSelectedCoworkingTypeSelector';

import { SwitchButton, TOptionValue } from '../UI/Buttons/SwitchButton';

import { FlexOfficeList } from './components/FlexOfficeList';
import { Place } from './components/Place';
import { switcherOptions } from './constants';
import { ECoworkingSwitcherType, IAvailableSpacesProps } from './types';
import { getSelectedCategory } from './utils/getSelectedCategory';
import { isPlaceVisible } from './utils/isPlaceVisible';

import * as styles from './styles.css';

export const AvailableSpaces: FC<IAvailableSpacesProps> = props => {
  const { className, isFlexOfficeAvailable, workplaces } = props;
  const { flex, fixed } = workplaces || {};

  const selectedCoworkingType = useSelectedCoworkingTypeSelector();

  const [selectedCategory, setSelectedCategory] = useState<TOptionValue>(
    getSelectedCategory(selectedCoworkingType, isFlexOfficeAvailable),
  );

  const handleSwitcherClick = useCallback<(value: TOptionValue) => void>(value => {
    setSelectedCategory(value);
  }, []);

  const isFlexPlaceVisible = useMemo<boolean>(() => isPlaceVisible(flex), [flex]);
  const isFixedPlaceVisible = useMemo<boolean>(() => isPlaceVisible(fixed), [fixed]);

  if (!isFlexOfficeAvailable && !isFlexPlaceVisible && !isFixedPlaceVisible) {
    return null;
  }

  return (
    <div className={className} data-nav-tab="spaces/Аренда пространств" id="spaces">
      <ArticleHeading2>Аренда в этом коворкинге</ArticleHeading2>
      {isFlexOfficeAvailable && (isFlexPlaceVisible || isFixedPlaceVisible) && (
        <SwitchButton
          className={styles['switch']}
          options={switcherOptions}
          value={selectedCategory}
          onClick={handleSwitcherClick}
        />
      )}
      <div className={styles['table']}>
        <div
          className={clsx(styles['item'], { [styles['visible']]: selectedCategory === ECoworkingSwitcherType.Office })}
          data-test-id="flex-office-list-wrapper"
        >
          <FlexOfficeList />
        </div>
        <div
          className={clsx(styles['item'], { [styles['visible']]: selectedCategory === ECoworkingSwitcherType.Places })}
          data-test-id="workplace-in-coworking-wrapper"
        >
          {flex && <Place {...flex} type={WorkplaceInCoworkingType.FlexibleWorkplace} />}
          {fixed && <Place {...fixed} type={WorkplaceInCoworkingType.FixedWorkplace} />}
        </div>
      </div>
    </div>
  );
};

AvailableSpaces.displayName = 'AvailableSpaces';
